/**
 * Enum defining public API endpoints that do not require authentication.
 *
 * @remarks
 * These endpoints are used by the AuthInterceptor to determine whether
 * to attach the Authorization header to a request.
 *
 * Public endpoints should NOT have Bearer tokens attached.
 */
export enum PublicEndpoint {
  /**
   * Login endpoint - used for initial authentication
   */
  LOGIN = '/auth/login',

  /**
   * Refresh token endpoint - used to obtain new access tokens
   */
  REFRESH = '/auth/refresh',

  /**
   * Forgot password endpoint - used to request password reset
   */
  FORGOT_PASSWORD = '/auth/forgot-password',

  /**
   * Signup endpoint - used to create a new account
   */
  SIGNUP = '/auth/signup',

  /**
   * Confirm email endpoint - used to validate onboarding token
   */
  CONFIRM_EMAIL = '/auth/confirm-email',

  /**
   * Resend confirmation email endpoint - used to generate a new onboarding link
   */
  RESEND_CONFIRMATION_EMAIL = '/auth/resend-confirmation-email',

  /**
   * Reset password endpoint - used to set new password with token
   */
  RESET_PASSWORD = '/auth/reset-password'
}

/**
 * Helper function to check if a URL matches a public endpoint.
 *
 * @param url - The request URL to check
 * @returns True if the URL is a public endpoint
 *
 * @example
 * ```typescript
 * isPublicEndpoint('http://localhost:3000/api/v1/auth/login') // true
 * isPublicEndpoint('/api/v1/auth/refresh') // true
 * isPublicEndpoint('/api/v1/products') // false
 * ```
 */
export function isPublicEndpoint(url: string): boolean {
  const path = new URL(url, 'http://localhost').pathname;
  return Object.values(PublicEndpoint).some(
    (endpoint) => path === endpoint || path.endsWith(endpoint)
  );
}
